import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { getSliderItems, deleteSliderItem } from '../../services/api';
import { Box, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Image } from 'antd'; // Keeping Antd's Image for specialized behavior
import DataTable from '../common/DataTable';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const SliderList = forwardRef(function SliderList({ onEdit, searchQuery = '', category = 'all' }, ref) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getSliderItems();
      const sorted = [...data].sort((a, b) => a.order - b.order);
      setItems(sorted);
    } catch (error) {
      toast.error('Failed to fetch slider items');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchItems
  }));

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteSliderItem(deleteId);
      toast.success('Slider item deleted successfully');
      fetchItems();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete slider item');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter items based on search and category
  const filteredItems = items.filter(item => {
    const matchesSearch =
      (item.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (item.subtitle?.toLowerCase() || '').includes(searchQuery.toLowerCase());

    // For now, category is just a UI placeholder, but we can implement it if fields exist
    return matchesSearch;
  });

  const getColumns = () => {
    const baseColumns = [
      {
        id: 'content',
        label: 'SLIDER CONTENT',
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Image
              src={row.imageUrl}
              alt="Slider"
              width={80}
              height={50}
              style={{ objectFit: 'cover', borderRadius: '6px' }}
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                {row.title}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {row.subtitle || 'No subtitle'}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        id: 'order',
        label: 'DISPLAY ORDER',
        align: 'center',
        width: '120px',
        render: (row) => (
          <Typography sx={{ fontWeight: 500, color: '#475569' }}>
            {row.order}
          </Typography>
        )
      },
      {
        id: 'actions',
        label: 'ACTIONS',
        align: 'right',
        width: '120px',
        render: (row) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              onClick={() => onEdit(row)}
              size="small"
              sx={{
                color: '#94a3b8',
                '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' },
                transition: 'all 0.2s'
              }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              onClick={() => setDeleteId(row.id)}
              size="small"
              sx={{
                color: '#94a3b8',
                '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' },
                transition: 'all 0.2s',
                ml: 1
              }}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ),
      },
    ];
    return baseColumns;
  };

  return (
    <>
      <DataTable
        premium={true}
        loading={loading}
        data={filteredItems}
        columns={getColumns()}
        emptyTitle="No Slider Items Found"
        emptySubtitle={searchQuery ? `No results for "${searchQuery}"` : "There are no slider items to display yet."}
      />
      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={confirmDelete}
        itemName="slider item"
        loading={deleteLoading}
      />
    </>
  );
});

export default SliderList;
